/**
 * 世界地图渲染（Canvas 2D）
 *
 * 视觉目标：**暖色火光 vs 冷色迷雾**。地图初始被靛蓝迷雾盖住，
 * 被点亮的地方像绘本里的油灯一样晕开暖橙光晕。
 *
 * 性能做法（目标：千元安卓机 60fps）：
 * 1. 区域光晕用**离屏 canvas 预渲染**的径向渐变精灵，每帧只 drawImage，不重建渐变；
 * 2. 迷雾层单独一张离屏画布，只在「控制权集合 / 迷雾浓度」变化时重建；
 * 3. 只在需要时启动 requestAnimationFrame —— 有动画才画，静止时零开销；
 * 4. 尊重 `prefers-reduced-motion`，动画全部退化为瞬时切换。
 */

import type { MatchSetup, OptionView, Region, Route, SimState } from "@chuandeng/shared";

const PADDING_RATIO = 0.14;
const BASE_RADIUS = 14;
const LABEL_MIN_FONT = 15;

/** 迷雾底色（与 CSS 令牌保持一致） */
const FOG_COLOR = "rgba(24, 30, 48, 0.94)";

type RegionVisual = "dark" | "camp" | "lit";

interface Placement {
  region: Region;
  /** 归一化坐标 */
  nx: number;
  ny: number;
  /** 屏幕坐标 */
  x: number;
  y: number;
  radius: number;
}

interface Pulse {
  x: number;
  y: number;
  startedAt: number;
  color: string;
  maxRadius: number;
}

export interface MapViewCallbacks {
  onRegionClick?(regionId: string): void;
  onRouteClick?(routeId: string): void;
  /** 渲染异常回调：宁可降级成静态地图，也不要留一块冻结的黑板 */
  onRenderError?(error: unknown): void;
}

export class MapView {
  private readonly ctx: CanvasRenderingContext2D;
  private readonly fog: HTMLCanvasElement;
  private readonly fogCtx: CanvasRenderingContext2D;
  private readonly sprites = new Map<string, HTMLCanvasElement>();

  private setup: MatchSetup | null = null;
  private state: SimState | null = null;
  private options: OptionView | null = null;

  private placements: Placement[] = [];
  private placementsById = new Map<string, Placement>();
  private previousEnlightenment = new Map<string, number>();

  private width = 0;
  private height = 0;
  private dpr = 1;
  private hoverRegionId: string | null = null;
  private hoverRouteId: string | null = null;

  private pulses: Pulse[] = [];
  private frame: number | null = null;
  private renderFailed = false;
  private fogSignature = "";
  private readonly reduceMotion: boolean;
  /** 引擎是否支持径向渐变（个别 WebView/无头浏览器没有，需要降级而不是崩掉） */
  private readonly radialGradient: boolean;
  private observer: ResizeObserver | null = null;
  private disposed = false;

  constructor(
    private readonly canvas: HTMLCanvasElement,
    private readonly callbacks: MapViewCallbacks = {},
  ) {
    const ctx = canvas.getContext("2d");
    if (!ctx) throw new Error("MapView: 浏览器不支持 Canvas 2D");
    this.ctx = ctx;
    this.fog = document.createElement("canvas");
    const fogCtx = this.fog.getContext("2d");
    if (!fogCtx) throw new Error("MapView: 无法创建迷雾层");
    this.fogCtx = fogCtx;
    this.reduceMotion =
      typeof window.matchMedia === "function" &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    this.radialGradient = supportsRadialGradient(this.fogCtx) && supportsRadialGradient(ctx);

    this.attachEvents();
    this.observeResize();
  }

  // ---------- 生命周期 ----------

  destroy(): void {
    this.disposed = true;
    this.observer?.disconnect();
    if (this.frame !== null) cancelAnimationFrame(this.frame);
    this.canvas.removeEventListener("pointerdown", this.handlePointerDown);
    this.canvas.removeEventListener("pointermove", this.handlePointerMove);
    this.canvas.removeEventListener("pointerleave", this.handlePointerLeave);
  }

  private observeResize(): void {
    if (typeof ResizeObserver === "function") {
      this.observer = new ResizeObserver(() => this.resize());
      this.observer.observe(this.canvas.parentElement ?? this.canvas);
    } else {
      window.addEventListener("resize", () => this.resize());
    }
    this.resize();
  }

  private resize(): void {
    const rect = this.canvas.getBoundingClientRect();
    const cssWidth = Math.max(240, Math.round(rect.width));
    const cssHeight = Math.max(240, Math.round(rect.height || rect.width * 0.62));
    this.dpr = Math.min(window.devicePixelRatio || 1, 2);

    this.width = cssWidth;
    this.height = cssHeight;
    this.canvas.width = Math.round(cssWidth * this.dpr);
    this.canvas.height = Math.round(cssHeight * this.dpr);
    this.ctx.setTransform(this.dpr, 0, 0, this.dpr, 0, 0);

    this.fog.width = this.canvas.width;
    this.fog.height = this.canvas.height;
    this.fogCtx.setTransform(this.dpr, 0, 0, this.dpr, 0, 0);

    this.layout();
    this.fogSignature = "";
    this.requestFrame();
  }

  // ---------- 数据更新 ----------

  /** 当前画布背板尺寸（性能悬浮层要显示它，判断高分屏是否真的按 dpr 放大） */
  get canvasSize(): { width: number; height: number } {
    return { width: this.canvas.width, height: this.canvas.height };
  }

  update(setup: MatchSetup, state: SimState, options: OptionView): void {
    this.setup = setup;
    this.state = state;
    this.options = options;

    // 首次挂载时 CSS 可能还没生效（量到的是 0），ResizeObserver 也不一定再触发一次，
    // 所以每次状态更新都核对一遍真实尺寸 —— 一次 rect 读取，代价远低于布局错位。
    const rect = this.canvas.getBoundingClientRect();
    if (Math.round(rect.width) !== this.width || Math.round(rect.height) !== this.height) {
      this.resize();
    }

    // 新点亮的区域播一次光晕扩散
    if (!this.reduceMotion) {
      for (const region of setup.map.regions) {
        const before = this.previousEnlightenment.get(region.id) ?? 0;
        const after = state.regions[region.id]?.enlightenment ?? 0;
        if (after > before) {
          const placement = this.placementsById.get(region.id);
          if (placement) {
            this.pulses.push({
              x: placement.x,
              y: placement.y,
              startedAt: performance.now(),
              color: state.regions[region.id]?.lit ? "#FFD166" : "#E8873A",
              maxRadius: placement.radius * 3.4,
            });
          }
        }
        this.previousEnlightenment.set(region.id, after);
      }
    }

    if (this.placements.length === 0) this.layout();
    this.requestFrame();
  }

  setHover(regionId: string | null, routeId: string | null = null): void {
    if (this.hoverRegionId === regionId && this.hoverRouteId === routeId) return;
    this.hoverRegionId = regionId;
    this.hoverRouteId = routeId;
    this.requestFrame();
  }

  // ---------- 布局 ----------

  private layout(): void {
    if (!this.setup) return;
    const regions = this.setup.map.regions;
    const spanX = this.width * (1 - PADDING_RATIO * 2);
    const spanY = this.height * (1 - PADDING_RATIO * 2);
    const offsetX = this.width * PADDING_RATIO;
    const offsetY = this.height * PADDING_RATIO;

    this.placements = regions.map((region, index) => {
      // 数据里有坐标就用它；没有则退化成确定性圆环布局（保证可玩，不追求好看）
      const fallbackAngle = (index / Math.max(1, regions.length)) * Math.PI * 2;
      const nx = region.x ?? 0.5 + Math.cos(fallbackAngle) * 0.34;
      const ny = region.y ?? 0.5 + Math.sin(fallbackAngle) * 0.34;
      const populationFactor = 0.86 + Math.min(region.population, 160) / 260;
      const radius = BASE_RADIUS * populationFactor * (this.width < 480 ? 0.78 : 1);
      return {
        region,
        nx,
        ny,
        x: offsetX + nx * spanX,
        y: offsetY + ny * spanY,
        radius,
      };
    });

    this.placementsById = new Map(this.placements.map((p) => [p.region.id, p]));
  }

  // ---------- 交互 ----------

  private attachEvents(): void {
    this.canvas.addEventListener("pointerdown", this.handlePointerDown);
    this.canvas.addEventListener("pointermove", this.handlePointerMove);
    this.canvas.addEventListener("pointerleave", this.handlePointerLeave);
  }

  private localPoint(event: PointerEvent): { x: number; y: number } {
    const rect = this.canvas.getBoundingClientRect();
    return { x: event.clientX - rect.left, y: event.clientY - rect.top };
  }

  private readonly handlePointerDown = (event: PointerEvent): void => {
    const { x, y } = this.localPoint(event);
    const region = this.hitRegion(x, y);
    if (region) {
      this.callbacks.onRegionClick?.(region.region.id);
      return;
    }
    const route = this.hitRoute(x, y);
    if (route) this.callbacks.onRouteClick?.(route.id);
  };

  private readonly handlePointerMove = (event: PointerEvent): void => {
    const { x, y } = this.localPoint(event);
    const region = this.hitRegion(x, y);
    const route = region ? null : this.hitRoute(x, y);
    const nextRegion = region?.region.id ?? null;
    const nextRoute = route?.id ?? null;
    if (nextRegion !== this.hoverRegionId || nextRoute !== this.hoverRouteId) {
      this.hoverRegionId = nextRegion;
      this.hoverRouteId = nextRoute;
      this.canvas.style.cursor = nextRegion || nextRoute ? "pointer" : "default";
      this.requestFrame();
    }
  };

  private readonly handlePointerLeave = (): void => {
    this.hoverRegionId = null;
    this.hoverRouteId = null;
    this.canvas.style.cursor = "default";
    this.requestFrame();
  };

  private hitRegion(x: number, y: number): Placement | null {
    let best: Placement | null = null;
    let bestDistance = Number.POSITIVE_INFINITY;
    for (const placement of this.placements) {
      const distance = Math.hypot(placement.x - x, placement.y - y);
      const hitRadius = Math.max(placement.radius + 10, 22); // 触控热区不小于 22px 半径
      if (distance <= hitRadius && distance < bestDistance) {
        best = placement;
        bestDistance = distance;
      }
    }
    return best;
  }

  private hitRoute(x: number, y: number): Route | null {
    if (!this.setup) return null;
    let best: Route | null = null;
    let bestDistance = 12;
    for (const route of this.visibleRoutes()) {
      const distance = this.distanceToRoute(route, x, y);
      if (distance < bestDistance) {
        best = route;
        bestDistance = distance;
      }
    }
    return best;
  }

  // ---------- 绘制 ----------

  private requestFrame(): void {
    if (this.disposed || this.frame !== null) return;
    this.frame = requestAnimationFrame((time) => {
      this.frame = null;
      this.draw(time);
      // 还有脉冲或路线动画就继续下一帧
      if (this.pulses.length > 0 || (!this.reduceMotion && this.hasUsableRoute())) {
        this.requestFrame();
      }
    });
  }

  private hasUsableRoute(): boolean {
    return (this.options?.routes ?? []).some((r) => r.usable);
  }

  private visibleRoutes(): Route[] {
    if (!this.setup || !this.state) return [];
    return this.setup.map.routes.filter((route) => {
      if (!route.hiddenByDefault) return true;
      return this.state!.revealedRouteIds.includes(route.id);
    });
  }

  private draw(time: number): void {
    if (this.renderFailed) return;
    try {
      this.drawScene(time);
    } catch (error) {
      // 一台画布渲染失败不能把整个页面拖死：停掉动画并上报，界面其余部分还能用
      this.renderFailed = true;
      this.canvas.dataset["renderError"] = String(error);
      console.error("地图渲染失败，动画已停止", error);
      this.callbacks.onRenderError?.(error);
    }
  }

  private drawScene(time: number): void {
    const { ctx } = this;
    if (!this.setup || !this.state || !this.options) return;

    ctx.clearRect(0, 0, this.width, this.height);
    this.drawBackdrop(ctx);
    this.drawRoutes(ctx, time);
    this.drawFog(ctx);
    this.drawRegions(ctx);
    this.drawPulses(ctx, time);
    this.drawHoverLabel(ctx);
  }

  private drawBackdrop(ctx: CanvasRenderingContext2D): void {
    // 夜色海洋：底部深蓝 + 顶部微光的呼吸感
    const gradient = ctx.createLinearGradient(0, 0, 0, this.height);
    gradient.addColorStop(0, "#2f3a58");
    gradient.addColorStop(0.55, "#232c45");
    gradient.addColorStop(1, "#1a2133");
    ctx.fillStyle = gradient;
    ctx.fillRect(0, 0, this.width, this.height);

    // 极淡的经纬网格，帮助建立"这是一张地图"的心理模型
    ctx.save();
    ctx.strokeStyle = "rgba(255, 255, 255, 0.05)";
    ctx.lineWidth = 1;
    const step = Math.max(48, this.width / 18);
    for (let x = step; x < this.width; x += step) {
      ctx.beginPath();
      ctx.moveTo(x, 0);
      ctx.lineTo(x, this.height);
      ctx.stroke();
    }
    for (let y = step; y < this.height; y += step) {
      ctx.beginPath();
      ctx.moveTo(0, y);
      ctx.lineTo(this.width, y);
      ctx.stroke();
    }
    ctx.restore();
  }

  private routePath(ctx: CanvasRenderingContext2D, route: Route): void {
    const from = this.placementsById.get(route.from);
    const to = this.placementsById.get(route.to);
    if (!from || !to) return;
    ctx.beginPath();
    ctx.moveTo(from.x, from.y);
    if (route.kind === "sea") {
      // 海路画成弧线：既区分陆路，也让远洋长航线绕开地图中央
      const midX = (from.x + to.x) / 2;
      const midY = (from.y + to.y) / 2;
      const dx = to.x - from.x;
      const dy = to.y - from.y;
      const length = Math.hypot(dx, dy) || 1;
      const bow = length * 0.16;
      ctx.quadraticCurveTo(midX - (dy / length) * bow, midY + (dx / length) * bow, to.x, to.y);
    } else {
      ctx.lineTo(to.x, to.y);
    }
  }

  private drawRoutes(ctx: CanvasRenderingContext2D, time: number): void {
    if (!this.options) return;
    const optionById = new Map(this.options.routes.map((r) => [r.route.id, r]));

    for (const route of this.visibleRoutes()) {
      const option = optionById.get(route.id);
      if (!option) continue;
      const closed = this.state!.closedRouteIds.includes(route.id);
      const hovered = this.hoverRouteId === route.id;

      ctx.save();
      this.routePath(ctx, route);

      const baseAlpha = option.usable ? 0.95 : 0.34;
      if (route.kind === "sea") {
        ctx.setLineDash(option.usable ? [10, 7] : [4, 8]);
        ctx.strokeStyle = option.usable
          ? "rgba(126, 200, 232, 0.95)"
          : "rgba(126, 200, 232, 0.34)";
      } else {
        ctx.setLineDash([]);
        ctx.strokeStyle = option.usable
          ? "rgba(214, 165, 106, 0.9)"
          : "rgba(214, 165, 106, 0.3)";
      }
      ctx.globalAlpha = hovered ? 1 : baseAlpha;
      ctx.lineWidth = hovered ? 4 : 2.5;
      ctx.stroke();

      // 可通行的路线加一层流光，提示"这里可以点"
      if (option.usable && !closed && !this.reduceMotion) {
        ctx.setLineDash([6, 16]);
        ctx.lineDashOffset = -((time / 26) % 1000);
        ctx.strokeStyle = "rgba(255, 209, 102, 0.95)";
        ctx.lineWidth = 3;
        ctx.globalAlpha = 0.9;
        ctx.stroke();
      }

      if (closed) {
        const from = this.placementsById.get(route.from);
        const to = this.placementsById.get(route.to);
        if (from && to) {
          const midX = (from.x + to.x) / 2;
          const midY = (from.y + to.y) / 2;
          ctx.setLineDash([]);
          ctx.globalAlpha = 1;
          ctx.fillStyle = "rgba(228, 87, 61, 0.95)";
          ctx.font = `700 ${Math.max(12, this.width / 60)}px system-ui, sans-serif`;
          ctx.textAlign = "center";
          ctx.textBaseline = "middle";
          ctx.fillText("✕", midX, midY);
        }
      }
      ctx.restore();
    }
  }

  private drawFog(ctx: CanvasRenderingContext2D): void {
    const { fogCtx } = this;
    if (!this.state) return;
    const mist = this.state.mist;
    const signature = `${this.width}x${this.height}:${mist}:${Object.entries(this.state.regions)
      .map(([id, r]) => `${id}${r.lit ? "L" : r.ignited ? "C" : "-"}`)
      .join("")}`;
    if (signature !== this.fogSignature) {
      this.fogSignature = signature;
      fogCtx.setTransform(1, 0, 0, 1, 0, 0);
      fogCtx.clearRect(0, 0, this.fog.width, this.fog.height);
      fogCtx.setTransform(this.dpr, 0, 0, this.dpr, 0, 0);
      fogCtx.fillStyle = FOG_COLOR;
      fogCtx.fillRect(0, 0, this.width, this.height);
      fogCtx.globalCompositeOperation = "destination-out";

      for (const placement of this.placements) {
        const regionState = this.state.regions[placement.region.id];
        if (!regionState) continue;
        const ratio = regionState.enlightenment / 100;
        const holeRadius = placement.radius * (1.4 + ratio * 4.4);
        const strength = regionState.lit ? 1 : 0.25 + ratio * 0.5;
        this.carveLight(fogCtx, placement, holeRadius, strength);
      }
      fogCtx.globalCompositeOperation = "source-over";
    }

    // 迷雾越浓，覆盖得越实
    ctx.save();
    ctx.globalAlpha = 0.42 + (mist / 100) * 0.5;
    ctx.drawImage(this.fog, 0, 0, this.width, this.height);
    ctx.restore();
  }

  private carveLight(
    ctx: CanvasRenderingContext2D,
    placement: Placement,
    radius: number,
    strength: number,
  ): void {
    // 没有径向渐变能力时退回"实心光池"：少一点柔光，但地图仍然可读
    if (!this.radialGradient) {
      ctx.globalAlpha = strength * 0.85;
      ctx.fillStyle = "#000";
      ctx.beginPath();
      ctx.arc(placement.x, placement.y, radius * 0.78, 0, Math.PI * 2);
      ctx.fill();
      ctx.globalAlpha = 1;
      return;
    }

    const gradient = ctx.createRadialGradient(
      placement.x,
      placement.y,
      placement.radius * 0.3,
      placement.x,
      placement.y,
      radius,
    );
    gradient.addColorStop(0, `rgba(0, 0, 0, ${strength})`);
    gradient.addColorStop(0.62, `rgba(0, 0, 0, ${strength * 0.55})`);
    gradient.addColorStop(1, "rgba(0, 0, 0, 0)");
    ctx.fillStyle = gradient;
    ctx.beginPath();
    ctx.arc(placement.x, placement.y, radius, 0, Math.PI * 2);
    ctx.fill();
  }

  private sprite(kind: RegionVisual, radius: number): HTMLCanvasElement {
    const bucket = Math.max(8, Math.round(radius / 4) * 4);
    const key = `${kind}:${bucket}`;
    const cached = this.sprites.get(key);
    if (cached) return cached;

    const size = bucket * 6;
    const canvas = document.createElement("canvas");
    canvas.width = size;
    canvas.height = size;
    const ctx = canvas.getContext("2d");
    if (ctx) {
      const centre = size / 2;
      if (this.radialGradient) {
        const gradient = ctx.createRadialGradient(centre, centre, bucket * 0.2, centre, centre, size / 2);
        if (kind === "lit") {
          gradient.addColorStop(0, "rgba(255, 249, 226, 0.98)");
          gradient.addColorStop(0.24, "rgba(255, 209, 102, 0.85)");
          gradient.addColorStop(0.55, "rgba(245, 165, 36, 0.34)");
          gradient.addColorStop(1, "rgba(245, 165, 36, 0)");
        } else if (kind === "camp") {
          gradient.addColorStop(0, "rgba(255, 214, 150, 0.9)");
          gradient.addColorStop(0.32, "rgba(232, 135, 58, 0.5)");
          gradient.addColorStop(1, "rgba(232, 135, 58, 0)");
        } else {
          gradient.addColorStop(0, "rgba(120, 132, 168, 0.55)");
          gradient.addColorStop(1, "rgba(120, 132, 168, 0)");
        }
        ctx.fillStyle = gradient;
        ctx.fillRect(0, 0, size, size);
      } else {
        // 降级：两层实心圆模拟柔和光晕
        const palette =
          kind === "lit"
            ? ["rgba(255, 209, 102, 0.55)", "rgba(255, 249, 226, 0.9)"]
            : kind === "camp"
              ? ["rgba(232, 135, 58, 0.4)", "rgba(255, 214, 150, 0.85)"]
              : ["rgba(120, 132, 168, 0.32)", "rgba(150, 162, 196, 0.7)"];
        ctx.fillStyle = palette[0]!;
        ctx.beginPath();
        ctx.arc(centre, centre, bucket * 2.6, 0, Math.PI * 2);
        ctx.fill();
        ctx.fillStyle = palette[1]!;
        ctx.beginPath();
        ctx.arc(centre, centre, bucket * 1.1, 0, Math.PI * 2);
        ctx.fill();
      }
    }
    this.sprites.set(key, canvas);
    return canvas;
  }

  private drawRegions(ctx: CanvasRenderingContext2D): void {
    if (!this.state) return;
    for (const placement of this.placements) {
      const regionState = this.state.regions[placement.region.id];
      if (!regionState) continue;
      const kind: RegionVisual = regionState.lit ? "lit" : regionState.ignited ? "camp" : "dark";
      if (this.radialGradient) {
        // 快路径：预渲染好的光晕精灵，每帧只 drawImage
        const glow = this.sprite(kind, placement.radius);
        const drawSize = placement.radius * 6;
        ctx.drawImage(glow, placement.x - drawSize / 2, placement.y - drawSize / 2, drawSize, drawSize);
      } else {
        // 降级路径：直接画两个同心圆当光晕（不依赖 drawImage 与径向渐变）
        const halo =
          kind === "lit"
            ? ["rgba(245, 165, 36, 0.30)", "rgba(255, 209, 102, 0.85)"]
            : kind === "camp"
              ? ["rgba(232, 135, 58, 0.26)", "rgba(255, 214, 150, 0.75)"]
              : ["rgba(120, 132, 168, 0.18)", "rgba(150, 162, 196, 0.55)"];
        ctx.beginPath();
        ctx.arc(placement.x, placement.y, placement.radius * 2.6, 0, Math.PI * 2);
        ctx.fillStyle = halo[0]!;
        ctx.fill();
        ctx.beginPath();
        ctx.arc(placement.x, placement.y, placement.radius * 1.25, 0, Math.PI * 2);
        ctx.fillStyle = halo[1]!;
        ctx.fill();
      }

      // 主体：实心圆 + 描边（颜色之外还有大小与描边的差异，不只靠颜色区分）
      ctx.beginPath();
      ctx.arc(placement.x, placement.y, placement.radius, 0, Math.PI * 2);
      ctx.fillStyle =
        kind === "lit" ? "#FFD166" : kind === "camp" ? "#E8873A" : "rgba(148, 160, 194, 0.9)";
      ctx.fill();
      ctx.lineWidth = kind === "dark" ? 1.5 : 2.5;
      ctx.strokeStyle = kind === "lit" ? "#FFF6DC" : "rgba(20, 24, 38, 0.55)";
      ctx.stroke();

      // 启蒙度环：进度一目了然，不依赖读数
      if (regionState.enlightenment > 0 && !regionState.lit) {
        ctx.beginPath();
        ctx.arc(
          placement.x,
          placement.y,
          placement.radius + 5,
          -Math.PI / 2,
          -Math.PI / 2 + (Math.PI * 2 * regionState.enlightenment) / 100,
        );
        ctx.strokeStyle = "rgba(255, 209, 102, 0.85)";
        ctx.lineWidth = 3;
        ctx.stroke();
      }

      this.drawLabel(ctx, placement, kind, regionState.enlightenment, regionState.ignited);
    }
  }

  private drawLabel(
    ctx: CanvasRenderingContext2D,
    placement: Placement,
    kind: RegionVisual,
    enlightenment: number,
    ignited: boolean,
  ): void {
    const fontSize = Math.max(LABEL_MIN_FONT, Math.min(placement.radius * 1.15, 22));
    ctx.save();
    ctx.font = `700 ${fontSize}px sans-serif`;
    ctx.textAlign = "center";
    ctx.textBaseline = "top";
    ctx.lineWidth = 3;
    ctx.strokeStyle = "rgba(12, 16, 26, 0.82)";
    ctx.fillStyle = kind === "dark" ? "rgba(226, 232, 245, 0.82)" : "#FFFDF6";
    const labelY = placement.y + placement.radius + 5;
    ctx.strokeText(placement.region.name, placement.x, labelY);
    ctx.fillText(placement.region.name, placement.x, labelY);

    // 状态行：文字 + 数字，色觉障碍下也能读懂
    const status =
      kind === "lit" ? "已点亮" : ignited ? `营地 ${Math.round(enlightenment)}` : `${Math.round(enlightenment)}`;
    ctx.font = `500 ${Math.max(11, fontSize * 0.72)}px sans-serif`;
    ctx.fillStyle = kind === "lit" ? "rgba(255, 209, 102, 0.95)" : "rgba(226, 232, 245, 0.62)";
    ctx.fillText(status, placement.x, labelY + fontSize + 1);
    ctx.restore();
  }

  private drawPulses(ctx: CanvasRenderingContext2D, time: number): void {
    if (this.pulses.length === 0) return;
    const alive: Pulse[] = [];
    for (const pulse of this.pulses) {
      const elapsed = time - pulse.startedAt;
      if (elapsed > 620) continue;
      alive.push(pulse);
      const t = elapsed / 620;
      const radius = pulse.maxRadius * (0.35 + t * 0.65);
      ctx.save();
      ctx.globalAlpha = (1 - t) * 0.55;
      ctx.beginPath();
      ctx.arc(pulse.x, pulse.y, radius, 0, Math.PI * 2);
      ctx.strokeStyle = pulse.color;
      ctx.lineWidth = 3 * (1 - t) + 1;
      ctx.stroke();
      ctx.restore();
    }
    this.pulses = alive;
  }

  private drawHoverLabel(ctx: CanvasRenderingContext2D): void {
    if (!this.hoverRegionId) return;
    const placement = this.placementsById.get(this.hoverRegionId);
    if (!placement) return;
    ctx.save();
    ctx.beginPath();
    ctx.arc(placement.x, placement.y, placement.radius + 9, 0, Math.PI * 2);
    ctx.strokeStyle = "rgba(255, 209, 102, 0.9)";
    ctx.lineWidth = 2;
    ctx.setLineDash([5, 5]);
    ctx.stroke();
    ctx.restore();
  }

  private distanceToRoute(route: Route, x: number, y: number): number {
    const from = this.placementsById.get(route.from);
    const to = this.placementsById.get(route.to);
    if (!from || !to) return Number.POSITIVE_INFINITY;

    if (route.kind === "land") {
      return distanceToSegment(x, y, from.x, from.y, to.x, to.y);
    }
    // 海路是二次贝塞尔，采样近似足够（命中判定的容差本来就有 12px）
    const midX = (from.x + to.x) / 2;
    const midY = (from.y + to.y) / 2;
    const dx = to.x - from.x;
    const dy = to.y - from.y;
    const length = Math.hypot(dx, dy) || 1;
    const bow = length * 0.16;
    const controlX = midX - (dy / length) * bow;
    const controlY = midY + (dx / length) * bow;

    let best = Number.POSITIVE_INFINITY;
    let prevX = from.x;
    let prevY = from.y;
    const steps = 16;
    for (let i = 1; i <= steps; i++) {
      const t = i / steps;
      const inv = 1 - t;
      const px = inv * inv * from.x + 2 * inv * t * controlX + t * t * to.x;
      const py = inv * inv * from.y + 2 * inv * t * controlY + t * t * to.y;
      best = Math.min(best, distanceToSegment(x, y, prevX, prevY, px, py));
      prevX = px;
      prevY = py;
    }
    return best;
  }
}

/** 探测 2D 上下文是否真的实现了 createRadialGradient */
function supportsRadialGradient(ctx: CanvasRenderingContext2D): boolean {
  return typeof ctx.createRadialGradient === "function";
}

function distanceToSegment(
  px: number,
  py: number,
  ax: number,
  ay: number,
  bx: number,
  by: number,
): number {
  const dx = bx - ax;
  const dy = by - ay;
  const lengthSquared = dx * dx + dy * dy;
  if (lengthSquared === 0) return Math.hypot(px - ax, py - ay);
  let t = ((px - ax) * dx + (py - ay) * dy) / lengthSquared;
  t = Math.max(0, Math.min(1, t));
  return Math.hypot(px - (ax + t * dx), py - (ay + t * dy));
}
